import React from 'react';
import { Alert, Box, Button, Paper, Skeleton, Tooltip, Typography } from '@mui/material';
import { deltaPct, fmtDelta, fmtInt, fmtPct, share } from '../../OnlineJournal/format';
import { GROUP_COLORS, WATCH_COLORS } from '../constants';
import { DocumentFilters } from '../types';
import { fmtHours, KIND_COLORS } from './analyticsFormat';
import { KpiResponse } from './analyticsTypes';

export type KpiView = 'cleanup' | 'leadtime';

interface Props {
  data: KpiResponse | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  /** Открыть в списке документы периода (когорту) с дополнительным условием */
  onCohort: (extra: Partial<DocumentFilters>) => void;
  /** Открыть в списке документы на контроле (без периода) */
  onWarnings: () => void;
  /** Перейти к подвкладке аналитики */
  onView: (view: KpiView) => void;
}

const BORDER = '#e2e8f0';

interface TileProps {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  delta?: React.ReactNode;
  color?: string;
  tooltip?: string;
  onClick?: () => void;
}

const Tile: React.FC<TileProps> = ({ label, value, hint, delta, color, tooltip, onClick }) => {
  const body = (
    <Paper
      variant="outlined" onClick={onClick}
      sx={{
        px: 1.75, py: 1.25, borderRadius: 2, minWidth: 0, height: '100%', boxSizing: 'border-box', borderColor: BORDER,
        cursor: onClick ? 'pointer' : 'default', transition: 'border-color .15s',
        '&:hover': onClick ? { borderColor: color || '#94a3b8' } : undefined
      }}
    >
      <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500 }} noWrap>{label}</Typography>
      <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: color || '#0f172a', lineHeight: 1.35, mt: 0.25 }} noWrap>{value}</Typography>
      {delta && <Box sx={{ mt: 0.25 }}>{delta}</Box>}
      {hint && <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', mt: 0.25 }} noWrap>{hint}</Typography>}
    </Paper>
  );
  return tooltip ? <Tooltip title={tooltip} arrow placement="top">{body}</Tooltip> : body;
};

/** Тон изменения: neutral - без оценки, badUp - рост плохо (ошибки, сроки), goodUp - рост хорошо */
type Tone = 'neutral' | 'badUp' | 'goodUp';

/** Изменение к прошлому периоду: '▲ +12% · было 1 234' */
const Delta: React.FC<{ cur: number | null; prev: number | null; tone?: Tone; fmt?: (v: number) => string }> = ({ cur, prev, tone = 'neutral', fmt = fmtInt }) => {
  if (cur === null || prev === null) return null;
  const d = deltaPct(cur, prev);
  const was = <Box component="span" sx={{ color: '#94a3b8', fontWeight: 400 }}>было {fmt(prev)}</Box>;
  if (d === null) {
    return <Typography sx={{ fontSize: '0.7rem' }} noWrap>{was}</Typography>;
  }
  const up = d > 0;
  const flat = Math.abs(d) < 0.05;
  let color = '#475569';
  if (flat) color = '#64748b';
  else if (tone === 'badUp') color = up ? '#dc2626' : '#16a34a';
  else if (tone === 'goodUp') color = up ? '#16a34a' : '#dc2626';
  return (
    <Typography sx={{ fontSize: '0.7rem', color, fontWeight: 600 }} noWrap>
      {flat ? '' : up ? '▲ ' : '▼ '}{flat ? 'без изменений' : fmtDelta(d)} {was}
    </Typography>
  );
};

/** Ключевые показатели периода против прошлого периода той же длины */
const AnalyticsKpi: React.FC<Props> = ({ data, loading, error, onRetry, onCohort, onWarnings, onView }) => {
  if (error && !data) {
    return (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={onRetry}>Повторить</Button>}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 1.25 }}>
        {Array.from({ length: 11 }).map((_, i) => <Skeleton key={i} variant="rounded" height={92} sx={{ borderRadius: 2 }} />)}
      </Box>
    );
  }

  const c = data.current;
  const p = data.previous;
  const auto = c.autoCancel + c.autoHide;
  const linesPerDoc = c.total ? c.lines / c.total : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={onRetry}>Повторить</Button>} sx={{ py: 0 }}>
          {error} На экране - данные прежнего запроса.
        </Alert>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 1.25, opacity: loading ? 0.6 : 1, transition: 'opacity .15s' }}>
        <Tile
          label="Создано документов" value={fmtInt(c.total)}
          delta={<Delta cur={c.total} prev={p.total} />}
          hint={`перемещений ${fmtInt(c.moves)} · возвратов ${fmtInt(c.returns)}`}
          tooltip="Документы, созданные в периоде. Клик - открыть их в списке"
          onClick={c.total ? () => onCohort({}) : undefined}
        />
        <Tile
          label="Через РЦ" value={fmtInt(c.via)} color={KIND_COLORS.via}
          delta={<Delta cur={c.via} prev={p.via} />}
          hint={`${fmtPct(share(c.via, c.moves))} перемещений`}
          tooltip="Перемещения через склад (РЦ). Клик - открыть их в списке"
          onClick={c.via ? () => onCohort({ type: '5', route: 'via' }) : undefined}
        />
        <Tile
          label="Приняты и завершены" value={fmtInt(c.done)} color={GROUP_COLORS.done}
          delta={<Delta cur={c.done} prev={p.done} />}
          hint={`${fmtPct(share(c.done, c.total))} · до «Завершен»: ${fmtInt(c.completed)}`}
          tooltip="Документы периода, которые сейчас в статусах приёмки или «Завершен». Клик - открыть их в списке"
          onClick={c.done ? () => onCohort({ statuses: ['done'] }) : undefined}
        />
        <Tile
          label="С ошибками" value={fmtInt(c.errors)} color={c.errors ? GROUP_COLORS.error : undefined}
          delta={<Delta cur={c.errors} prev={p.errors} tone="badUp" />}
          hint={`${fmtPct(share(c.errors, c.total))} созданных`}
          tooltip="Документы периода, которые сейчас в статусах ошибки (отклонен, ошибка остатка, ошибка 1С). Клик - открыть их в списке"
          onClick={c.errors ? () => onCohort({ statuses: ['error'] }) : undefined}
        />
        <Tile
          label="В работе" value={fmtInt(c.active)} color={GROUP_COLORS.active}
          delta={<Delta cur={c.active} prev={p.active} />}
          hint={`${fmtPct(share(c.active, c.total))} созданных`}
          tooltip="Документы периода, которые ещё не дошли до приёмки. Клик - открыть их в списке"
          onClick={c.active ? () => onCohort({ statuses: ['active'] }) : undefined}
        />
        <Tile
          label="Черновики" value={fmtInt(c.drafts)} color={c.drafts ? GROUP_COLORS.draft : undefined}
          delta={<Delta cur={c.drafts} prev={p.drafts} tone="badUp" />}
          hint={`скрыто документов: ${fmtInt(c.hidden)}`}
          tooltip="Документы периода, которые так и остались черновиками. Клик - открыть их в списке"
          onClick={c.drafts ? () => onCohort({ statuses: ['draft'] }) : undefined}
        />
        <Tile
          label="Медиана до «Завершен»" value={fmtHours(c.p50Hours)}
          delta={<Delta cur={c.p50Hours} prev={p.p50Hours} tone="badUp" fmt={fmtHours} />}
          hint={`90% - до ${fmtHours(c.p90Hours)} · завершено ${fmtInt(c.completed)}`}
          tooltip="Время от создания до первого статуса «Завершен» по истории статусов: половина документов уложилась в медиану, 90% - в правое значение. Клик - подробно во вкладке «Сроки обработки»"
          onClick={() => onView('leadtime')}
        />
        <Tile
          label="Позиций в документах" value={fmtInt(c.lines)}
          delta={<Delta cur={c.lines} prev={p.lines} />}
          hint={`${fmtInt(c.qty)} уп. · ${linesPerDoc.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} на документ`}
        />
        <Tile
          label="Отправители / получатели" value={`${fmtInt(c.senders)} / ${fmtInt(c.recipients)}`}
          hint={`было ${fmtInt(p.senders)} / ${fmtInt(p.recipients)}`}
          tooltip="Сколько разных аптек и складов отправляли и получали документы периода"
        />
        <Tile
          label="Автоочистка" value={fmtInt(auto)} color={auto ? '#b45309' : undefined}
          delta={<Delta cur={auto} prev={p.autoCancel + p.autoHide} tone="badUp" />}
          hint={`отменено ${fmtInt(c.autoCancel)} · скрыто ${fmtInt(c.autoHide)}`}
          tooltip="Документы, которые ночная автоочистка отменила или скрыла в периоде (по дате события). Клик - подробно во вкладке «Автоочистка»"
          onClick={() => onView('cleanup')}
        />
        <Tile
          label="На контроле сейчас" value={fmtInt(data.now.warnings)} color={data.now.warnings ? WATCH_COLORS.warn : undefined}
          hint={`критично ${fmtInt(data.now.critical)} · под наблюдением ${fmtInt(data.now.watched)}`}
          tooltip="Текущее состояние без учёта периода: документы, которые скоро отменит или скроет автоочистка. Клик - открыть их в списке"
          onClick={data.now.warnings ? () => onWarnings() : undefined}
        />
      </Box>
    </Box>
  );
};

export default React.memo(AnalyticsKpi);
